/**
 * ConfirmDialog
 *
 * Reusable confirmation dialog that replaces window.confirm().
 * Follows the dark-theme modal pattern from TokenConfirmDialog.
 */

import React from 'react';
import { AlertTriangle, Info, X, Check, Loader2 } from 'lucide-react';

// ============================================================================
// Types
// ============================================================================

export type ConfirmDialogVariant = 'danger' | 'warning' | 'info';

export interface ConfirmDialogProps {
  isOpen: boolean;
  onConfirm: () => void;
  onCancel: () => void;
  title: string;
  message: string;
  confirmText?: string;
  cancelText?: string;
  variant?: ConfirmDialogVariant;
  loading?: boolean;
}

// ============================================================================
// Helpers
// ============================================================================

function getVariantIcon(variant: ConfirmDialogVariant): React.ReactElement {
  switch (variant) {
    case 'danger':
      return <AlertTriangle className="h-5 w-5 text-red-400" />;
    case 'warning':
      return <AlertTriangle className="h-5 w-5 text-amber-400" />;
    case 'info':
    default:
      return <Info className="h-5 w-5 text-purple-400" />;
  }
}

function getVariantIconBg(variant: ConfirmDialogVariant): string {
  switch (variant) {
    case 'danger':
      return 'bg-red-900/30';
    case 'warning':
      return 'bg-amber-900/30';
    case 'info':
    default:
      return 'bg-purple-900/30';
  }
}

function getConfirmButtonClass(variant: ConfirmDialogVariant): string {
  switch (variant) {
    case 'danger':
      return 'bg-red-600 hover:bg-red-700 focus:ring-red-500';
    case 'warning':
      return 'bg-amber-600 hover:bg-amber-700 focus:ring-amber-500';
    case 'info':
    default:
      return 'bg-primary-500 hover:bg-primary-600 focus:ring-primary-500';
  }
}

// ============================================================================
// Component
// ============================================================================

export function ConfirmDialog({
  isOpen,
  onConfirm,
  onCancel,
  title,
  message,
  confirmText = 'Confirm',
  cancelText = 'Cancel',
  variant = 'info',
  loading = false,
}: ConfirmDialogProps): React.ReactElement | null {
  if (!isOpen) return null;

  const handleBackdropClick = (e: React.MouseEvent<HTMLDivElement>) => {
    if (e.target === e.currentTarget && !loading) {
      onCancel();
    }
  };

  const confirmButtonClass = getConfirmButtonClass(variant);
  const iconBgClass = getVariantIconBg(variant);

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4"
      onClick={handleBackdropClick}
    >
      <div className="bg-secondary-800 rounded-xl max-w-md w-full shadow-2xl animate-in fade-in zoom-in-95 duration-200">
        {/* Header */}
        <div className="p-4 sm:p-6 pb-4 border-b border-secondary-700">
          <div className="flex items-center gap-3">
            <div className={`p-2 ${iconBgClass} rounded-lg flex-shrink-0`}>
              {getVariantIcon(variant)}
            </div>
            <h3 className="text-lg font-semibold text-white">{title}</h3>
          </div>
        </div>

        {/* Content */}
        <div className="p-4 sm:p-6">
          <p className="text-sm text-secondary-300">{message}</p>
        </div>

        {/* Footer */}
        <div className="p-4 sm:p-6 pt-0 flex gap-3">
          <button
            onClick={onCancel}
            disabled={loading}
            className="flex-1 py-2.5 px-4 border border-secondary-600 rounded-lg text-secondary-300 hover:bg-secondary-700 transition-colors min-h-[44px] disabled:opacity-50 disabled:cursor-not-allowed focus:outline-none focus:ring-2 focus:ring-secondary-500"
          >
            <X className="h-4 w-4 inline mr-1.5" />
            {cancelText}
          </button>
          <button
            onClick={onConfirm}
            disabled={loading}
            className={`flex-1 py-2.5 px-4 ${confirmButtonClass} disabled:opacity-50 disabled:cursor-not-allowed text-white rounded-lg transition-colors min-h-[44px] focus:outline-none focus:ring-2`}
          >
            {loading ? (
              <Loader2 className="h-4 w-4 inline mr-1.5 animate-spin" />
            ) : (
              <Check className="h-4 w-4 inline mr-1.5" />
            )}
            {loading ? 'Processing...' : confirmText}
          </button>
        </div>
      </div>
    </div>
  );
}

export default ConfirmDialog;
